import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Globe, Heart, Image as ImageIcon, Inbox, LayoutDashboard, Loader2, LogOut, Package, Settings, Tags } from "lucide-react";
import { api, setToken } from "@/lib/api";
import AdminDashboard from "./AdminDashboard";
import AdminWebsite from "./AdminWebsite";
import AdminMedia from "./AdminMedia";
import AdminEnquiries from "./AdminEnquiries";
import AdminProducts from "./AdminProducts";
import AdminCategories from "./AdminCategories";
import AdminOrders from "./AdminOrders";
import AdminSettings from "./AdminSettings";

export default function AdminApp() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!localStorage.getItem("gl_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    api
      .get("/admin/me")
      .then((r) => setAdmin(r.data))
      .catch(() => {
        setToken("");
        navigate("/admin/login", { replace: true });
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/admin/logout");
    } catch {
      /* token may already be gone */
    }
    setToken("");
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 size={28} className="animate-spin text-rose" />
      </div>
    );
  }

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "website", label: "Website", icon: Globe },
    { id: "media", label: "Media", icon: ImageIcon },
    { id: "products", label: "Products", icon: Package },
    { id: "categories", label: "Categories", icon: Tags },
    { id: "orders", label: "Orders", icon: CreditCard },
    { id: "enquiries", label: "Enquiries", icon: Inbox },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-cream font-sans text-ink antialiased">
      <header className="sticky top-0 z-40 border-b border-line/70 bg-cream/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-2">
            <Heart size={18} className="fill-rose text-rose" />
            <span className="font-serif text-xl font-semibold">Gifting Loves</span>
            <span className="rounded-full bg-rose/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-rose">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-email" className="hidden text-xs text-[#5C5C5C] sm:block">
              {admin?.email}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-xs font-medium text-ink transition-colors hover:border-rose hover:text-rose"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto px-4 pb-3 md:px-6">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id
                  ? "bg-ink text-cream"
                  : "border border-line text-[#5C5C5C] hover:border-rose hover:text-rose"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        {tab === "dashboard" && <AdminDashboard onNavigate={setTab} />}
        {tab === "website" && <AdminWebsite />}
        {tab === "media" && <AdminMedia />}
        {tab === "products" && <AdminProducts />}
        {tab === "categories" && <AdminCategories />}
        {tab === "orders" && <AdminOrders />}
        {tab === "enquiries" && <AdminEnquiries />}
        {tab === "settings" && <AdminSettings />}
      </main>
    </div>
  );
}
